import React, { Component } from 'react';
import _ from 'lodash';

import LegosManager from '../LegosManager';
import { SearchFilterButton } from '../../components/common/SearchFilterButton';
import { TypeTag } from '../../components/lego/TypeTag';
import { IconButton } from '../../components/common/IconButton';
import ModalCreateTasksFromLegos from '../../components/lego/ModalCreateTasksFromLegos';
import FuseboxComparison from '../../components/lego/fusebox-editor/FuseboxComparison';
import { LegoFiltersBar } from '../../components/common/LegoFiltersBar';
import { LegoPreview } from '../../components/lego/LegoPreview';
import ImageClusterPreview from '../../components/images/ImageClusterPreview';
import MultiButtonSwitch from '../../components/common/editors/MultiButtonSwitch';
import urls from '../../../../components/interop/urls';
import { ImagePreview } from '../../components/images/ImagePreview';
import StableUrlsWidget from '../../components/lego/stable-urls/StableUrlsWidget';
import PublishedFuseboxThumbnail from '../../components/lego/fusebox-editor/PublishedFuseboxThumbnail';
import ModelGenerationsTimeline from '../../components/lego/ModelGenerationsTimeline';
import { Icon } from '../../components/common/Icon';

const FAST_METRICS_UI = 'https://metrics2.rewise.ai/ui/fast-metrics';

/** Self-contained (admin bundle only); aligns with site slugs via NFD + strip combining marks. */
function sanitizarCaracteresEspecialesForMetrics(s) {
  let r = s;
  if (r) {
    r = String(r).trim();
    r = r.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    r = r.toLowerCase();
    r = r.replace(/[^\w-\s]/g, '');
    r = r.trim().replace(/\s/g, '-');
    r = r.replace(/--+/g, '-');
  }
  return r;
}

function getSlugForMetrics(str) {
  return str == null || str === '' ? '' : sanitizarCaracteresEspecialesForMetrics(String(str).trim());
}

function yearPrefixFromStableUrlPath(url) {
  const m = String(url || '').match(/^(\d{4})/);
  return m ? m[1] : null;
}

/** Same shape as FastMetricsProxy / traffic stats: `${brandSlug}-${model.slug}` for landing_url_model. */
function makeModelSlugForMetrics({ marca, slug }) {
  if (!marca || slug == null || slug === '') {
    return null;
  }
  return `${getSlugForMetrics(marca)}-${slug}`;
}

function buildFastMetricsHashFromModelYearPairs(pairs) {
  const orBranches = pairs.map(([makeModelSlug, year]) => ({
    $and: [
      { field: 'landing_url_model', param: makeModelSlug, op: '==' },
      { field: 'landing_url_year', param: year, op: '==' },
    ],
  }));
  const payload = {
    query: {
      groupBy: [null, null],
      measure: [],
      filters: [{ $or: orBranches }],
      limit: 100,
    },
  };
  return encodeURIComponent(JSON.stringify(payload));
}

function FuseboxTypeSelector({value, onChange}) {
  const allTypes = ['fusebox', 'fuseboxTemplate'];

  if(!value)
    value = {$in: []};

  let {$in: types} = value;

  if(!_.difference(allTypes, types).length) {
    types = [];
  }

  let onChangeHandler = (type) => {
    if(_.includes(types, type)) {
      types = _.without(types, type);
    } else {
      types = [... types, type];
    }
    onChange({$in: types.length ? types : allTypes});
  }


  return <span>
    {_.map(allTypes, t =>
      <SearchFilterButton key={t} onChange={() => onChangeHandler(t)} value={_.includes(types, t)}>
        <TypeTag key={t} type={t}/>
      </SearchFilterButton>)
    }
  </span>;
}

const PREVIEW_CLUSTER = 'Cluster';
const PREVIEW_DIAGRAM = 'Diagram';
const PREVIEW_FUSEBOX = 'Fusebox';


export default class FuseboxManager extends LegosManager {
  constructor(... args) {
    super(... args);

    // Skip data.fuses to transfer 10X less data
    this.state.extraQueryOptions = {
      type: {$in: ['fusebox', 'fuseboxTemplate']},
      $select: {"data.fuses": {$slice: 1}, "data.boxDescription": false}
    }

    this.state.previewType = PREVIEW_DIAGRAM;
    // this.state.previewType = PREVIEW_CLUSTER;

    this.rankService = this.service('/services/legos/fusebox-rank');

    // setTimeout(() => this.openCompareModal("61ad203f963f62000474813b", "61ad225e963f620004748146"),100);
  }

  getRankQuery() {
    // locales and context are not needed, as they are queried in the server
    const legoData = this.state.objects.map(({_id, locales, context}) => ({_id}));
    return {query: {legos: legoData, rankCriteria: this.state.rankCriteria}};
  }

  buildObjectQuery() {
    let originalQuery = super.buildObjectQuery();

    if(_.keys(originalQuery).length === 2) {
      originalQuery.$limit = 20;
    }
    return originalQuery;
  }

  getColumnsDefinition(objects) {
    // TODO: Coupled with LegosManager columnDefinition. Might break
    let [type, sem, ... rest] = super.getColumnsDefinition(objects);

    const pre = rest[rest.length-1];
    pre.content = <span>
      Preview: <MultiButtonSwitch value={this.state.previewType} onChange={(v) => this.setState({previewType: v})} options={[PREVIEW_DIAGRAM, PREVIEW_CLUSTER, PREVIEW_FUSEBOX]}/>
    </span>;

    return [type, ... rest];
  }

  getSelectionButtons(selection) {
    let compareBtn = null;
    if(selection.length === 2) {
      let [a,b] = selection;
      compareBtn = <IconButton icon={'compare'} title="Compare fuseboxes" onClick={() => this.openCompareModal(a._id, b._id)}/>
    }

    return <>
      { super.getSelectionButtons(selection) }
      { compareBtn }
      { selection.length ? <IconButton icon={'query_stats'} title={'Open Fast Metrics for selected fuseboxes (make-model slug × year)'} onClick={() => this.openFastMetricsForSelection(selection)}/> : null }
      <IconButton icon={'add_task'} title={'Create tasks for selected fuseboxes'} onClick={() => this.openCreateTasksModal(selection)}/>
    </>
  }

  openFastMetricsForSelection(selection) {
    this.runAsync(this.loadFastMetricsUrlForSelection(selection), 'Resolving stable URLs…');
  }

  async loadFastMetricsUrlForSelection(selection) {
    const stableUrlsService = this.service('/services/stable-urls/collection');
    const fuseboxIds = [];

    for (const lego of selection) {
      if (lego.type === 'fusebox') {
        fuseboxIds.push(String(lego._id));
      } else if (lego.type === 'fuseboxTemplate') {
        const instances = await this.views.find({
          query: {
            type: 'fusebox',
            'data.templateId': String(lego._id),
            $limit: 10000,
            $select: { _id: 1 },
          },
        });
        for (const fb of instances) {
          fuseboxIds.push(String(fb._id));
        }
      }
    }

    const uniqueIds = _.uniq(fuseboxIds);
    if (!uniqueIds.length) {
      alert('No fusebox legos in the selection (templates may have no linked fuseboxes).');
      return;
    }

    const stableUrls = await stableUrlsService.find({
      query: {
        type: 'fusebox',
        id: { $in: uniqueIds },
        $limit: 50000,
        $select: { key: 1, url: 1, id: 1 },
      },
    });

    const idModelosFromUrls = _.uniq(_.compact(_.map(stableUrls, 'key')));
    const idModeloToLandingSlug = await this.getIdModeloToMakeModelSlugMap(idModelosFromUrls);

    const pairSet = new Set();
    for (const doc of stableUrls) {
      const year = yearPrefixFromStableUrlPath(doc.url);
      if (!year || !doc.key) {
        continue;
      }
      const landingSlug = idModeloToLandingSlug[doc.key];
      if (!landingSlug) {
        continue;
      }
      pairSet.add(`${landingSlug}\0${year}`);
    }

    const pairs = [...pairSet].map((s) => {
      const i = s.indexOf('\0');
      return [s.slice(0, i), s.slice(i + 1)];
    });

    if (!pairs.length) {
      alert('No stable fusebox URLs found for the selected legos, or model keys could not be mapped to make-model slugs.');
      return;
    }

    const hash = buildFastMetricsHashFromModelYearPairs(pairs);
    window.open(`${FAST_METRICS_UI}#${hash}`, '_blank', 'noopener,noreferrer');
  }

  async getIdModeloToMakeModelSlugMap(idModelos) {
    const cacheKey = _.sortBy(_.uniq(idModelos)).join('\0');
    if (!this._idModeloToMakeModelSlugCache) {
      this._idModeloToMakeModelSlugCache = {};
    }
    if (this._idModeloToMakeModelSlugCache[cacheKey]) {
      return this._idModeloToMakeModelSlugCache[cacheKey];
    }
    if (!idModelos.length) {
      return {};
    }
    const modelDocs = await this.service('/services/data/models/all').find({
      query: {
        idModelo: { $in: _.uniq(idModelos) },
        toDelete: { $ne: true },
        $select: { idModelo: 1, marca: 1, slug: 1, toDelete: 1 },
        $limit: 5000,
      },
    });
    const map = {};
    for (const doc of modelDocs) {
      if (doc.toDelete || !doc.idModelo) {
        continue;
      }
      const landingSlug = makeModelSlugForMetrics({ marca: doc.marca, slug: doc.slug });
      if (landingSlug) {
        map[doc.idModelo] = landingSlug;
      }
    }
    this._idModeloToMakeModelSlugCache[cacheKey] = map;
    return map;
  }

  openCompareModal(fuseboxA, fuseboxB) {
    this.openModal(
      <FuseboxComparison
        fuseboxIdA={fuseboxA}
        fuseboxIdB={fuseboxB}
        onMergeFuseboxLegos={(idDeleted, idMergedInto) => {
          if(confirm(`Fusebox with ID ${idDeleted} will be deleted.\n\nIts context/sources will be merged with fusebox ${idMergedInto}.\n\nARE YOU REALLY SURE?`)) {
            this.runAsync(async () => {
              let toDelete = await this.views.get(idDeleted);
              let toEdit = await this.views.get(idMergedInto);

              toEdit.context = _.sortBy([... toEdit.context, ... toDelete.context], 'modelId', 'year');
              toEdit.source = [... toEdit.source, ... toDelete.source];
              toEdit.notes = [... toEdit.notes, {
                content: `Lego ID ${idDeleted} was merged into this and deleted.`,
                userId: this.getLoggedUserSignature().id,
                createdAt: new Date()
              }
              ];

              await this.edit(toEdit, toEdit);
              await this.delete(toDelete, true);

              console.log(`Merged ${idDeleted} into ${idMergedInto}. Resulting lego: `, toEdit);

              this.selectNone();

              this.closeModal();
            })
          }
        }}
      />,
      { fullScreen: true, title: 'Compare fusebox legos' });
  }

  openCreateTasksModal(selection) {
    this.modalActionsBus.emit('open', <ModalCreateTasksFromLegos legos={selection} onCancel={() => this.modalActionsBus.emit('close')}/>);
  }

  filterByFuseDescription(desc = '') {
    let regex = prompt('Enter a regex to filter in the fuse description', desc);
    if(regex) {
      this.onFiltersChange({...this.state.facetFilters, 'data.fuses.description': {$regex: regex}});
    }
  }

  getFacetFiltersComponent() {
    let facetFilters = this.state.facetFilters || {};

    const fields = ['locales', 'context', 'state', 'labels', '_id'];
    const onTypeChange = (f) => this.onFiltersChange({... facetFilters, type: f});

    let desc = facetFilters['data.fuses.description'];

    return <div>
      <span className={''}>
        <FuseboxTypeSelector value={facetFilters.type} onChange={onTypeChange}/>
      </span>

      <span className={'mr-3'}>
        <IconButton icon={'find_in_page'} level={desc ? 'primary' : 'secondary'} title="Search inside fuse description with a regex" onClick={() => this.filterByFuseDescription(desc?.$regex)}/>
        {desc ? <span className={'badge badge-primary badge-outline'}>/{desc.$regex}/</span> : null}
        {desc ? <IconButton icon={'clear'} level={'danger'} onClick={() => this.onFiltersChange(_.omit(facetFilters, 'data.fuses.description'))}/> : null}
      </span>

      <LegoFiltersBar fields={fields} onChange={this.onFiltersChange.bind(this)} filter={facetFilters}/>

      <StableUrlsWidget type="fusebox" />
    </div>
  }
  
  showStats() {
    return <ModelGenerationsTimeline context={this.state.facetFilters?.context} />
  }

  onAddNewClick(overrides) {
    // Override defaults for new Button
    super.onAddNewClick({
      type: 'fusebox',
      intentions: ["spec"],
      semantics: ["DIAGRAMA_DE_FUSIBLES"],
      data: [{fuses: []}],
      state: 'inprogress'
    })
  }

  getObjectColumns(l, definition) {
    // Ignore intention and sem columns, as all fusebox have the same values there
    let [type, sem, ... rest] = super.getObjectColumns(l, definition);

    let preview = rest.slice(-1)[0];
    if(this.state.previewType === PREVIEW_CLUSTER) {
      let clusterId = l.data?.[0]?.imageClusters;
      if(clusterId?.length) {
        preview = <td key={'pre'} className={'data-column'} onClick={() => this.openEdit(l)}>
          <ImageClusterPreview clusterId={clusterId[0] || clusterId} maxImages={5}/>
        </td>
      }
    } else if(this.state.previewType === PREVIEW_DIAGRAM || ((l.type === 'fuseboxTemplate' || l.state !== 'published') && this.state.previewType === PREVIEW_FUSEBOX)) {
      preview = <td key={'pre'} className={'data-column'} onClick={() => this.openEdit(l)}>
        <LegoPreview lego={l} textFilter={this.state.textFilter}/>
      </td>
    } else if(this.state.previewType === PREVIEW_FUSEBOX) {
      preview = <td key={'pre'} className={'data-column'} onClick={() => this.openEdit(l)}>
        {(l.data || []).map((box, i) => {
          if(box.hidden) {
            return  <div className='inline-block small p-1 border border-danger bg-light-danger rounded' style={{width: '100px'}}>
              <Icon icon={'visibility_off'} level={'danger'}/>
              {box.boxName}
            </div>;
          }
          let hiddenBeforeIndex = l.data.slice(0, i).filter(b => b.hidden).length;
          return <PublishedFuseboxThumbnail context={l.context[0]} locale={l.locales?.[0]} boxIndex={i - hiddenBeforeIndex} key={i} />;
        })}
      </td>
    }

    return [type, ... rest.slice(0,-1), preview];
  }
}
